// Google Drive sync: keeps one JSON file ("invoice-studio-data.json") in your own Drive.
// Uses the narrow drive.file scope – the app can only see files it created, nothing else in your Drive.

export const DRIVE_FILE_NAME = "invoice-studio-data.json";
const SCOPE = "https://www.googleapis.com/auth/drive.file";
const API = "https://www.googleapis.com/drive/v3";
const UPLOAD = "https://www.googleapis.com/upload/drive/v3";

let gisPromise;
export function loadGis() {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.google?.accounts?.oauth2) return Promise.resolve();
  gisPromise ||= new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => {
      gisPromise = null;
      reject(new Error("Couldn’t load Google sign-in. Check your internet connection or ad-blocker."));
    };
    document.head.appendChild(s);
  });
  return gisPromise;
}

// Opens Google's popup (must be called from a click) and resolves with an access token.
export async function requestToken(clientId, { prompt = "", hint } = {}) {
  await loadGis();
  return new Promise((resolve, reject) => {
    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: SCOPE,
      hint,
      callback: (res) => (res.error ? reject(new Error(res.error_description || res.error)) : resolve({ token: res.access_token, expiresAt: Date.now() + (Number(res.expires_in) || 3600) * 1000 - 60000 })),
      error_callback: (err) => reject(new Error(err?.message || err?.type || "Google sign-in was closed")),
    });
    client.requestAccessToken({ prompt });
  });
}

async function api(token, url, opts = {}) {
  const res = await fetch(url, { ...opts, headers: { Authorization: `Bearer ${token}`, ...(opts.headers || {}) } });
  if (res.status === 401) {
    const e = new Error("Google session expired");
    e.code = 401;
    throw e;
  }
  if (!res.ok) throw new Error(`Google Drive error ${res.status}: ${(await res.text()).slice(0, 200)}`);
  return res;
}

const META_FIELDS = "id,name,modifiedTime,appProperties";

export async function findFile(token) {
  const q = encodeURIComponent(`name='${DRIVE_FILE_NAME}' and trashed=false`);
  const res = await api(token, `${API}/files?q=${q}&spaces=drive&orderBy=modifiedTime desc&fields=files(${META_FIELDS})`);
  return (await res.json()).files?.[0] || null;
}

export async function getMeta(token, id) {
  const res = await api(token, `${API}/files/${id}?fields=${META_FIELDS}`);
  return res.json();
}

export async function download(token, id) {
  const res = await api(token, `${API}/files/${id}?alt=media`);
  return res.json();
}

export async function upload(token, id, data, appProperties) {
  const boundary = "invoice-studio-" + Math.random().toString(36).slice(2);
  const meta = { name: DRIVE_FILE_NAME, mimeType: "application/json", appProperties };
  const body =
    `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n${JSON.stringify(meta)}\r\n` +
    `--${boundary}\r\nContent-Type: application/json\r\n\r\n${JSON.stringify(data)}\r\n--${boundary}--`;
  const url = id ? `${UPLOAD}/files/${id}?uploadType=multipart&fields=${META_FIELDS}` : `${UPLOAD}/files?uploadType=multipart&fields=${META_FIELDS}`;
  const res = await api(token, url, { method: id ? "PATCH" : "POST", headers: { "Content-Type": `multipart/related; boundary=${boundary}` }, body });
  return res.json();
}

// ---- tax document files: one Drive file each, tagged with the document id ----
export async function uploadDocFile(token, { docId, name, blob, encrypted }) {
  const boundary = "invoice-studio-" + Math.random().toString(36).slice(2);
  const meta = {
    name: encrypted ? `invoice-studio-doc-${docId}.bin` : `invoice-studio-doc-${docId}-${name}`,
    mimeType: encrypted ? "application/octet-stream" : blob.type || "application/octet-stream",
    appProperties: { app: "invoice-studio", docId },
  };
  const body = new Blob([
    `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n${JSON.stringify(meta)}\r\n--${boundary}\r\nContent-Type: ${meta.mimeType}\r\n\r\n`,
    blob,
    `\r\n--${boundary}--`,
  ]);
  const res = await api(token, `${UPLOAD}/files?uploadType=multipart&fields=id`, { method: "POST", headers: { "Content-Type": `multipart/related; boundary=${boundary}` }, body });
  return (await res.json()).id;
}

export async function downloadDocFile(token, id) {
  const res = await api(token, `${API}/files/${id}?alt=media`);
  return res.blob();
}

export async function deleteDriveFile(token, id) {
  try {
    await api(token, `${API}/files/${id}`, { method: "DELETE" });
  } catch (e) {
    if (!/ 404/.test(e.message)) throw e; // already gone is fine
  }
}

export async function whoAmI(token) {
  try {
    const res = await api(token, `${API}/about?fields=user(emailAddress,displayName)`);
    return (await res.json()).user?.emailAddress || "";
  } catch {
    return "";
  }
}

export function deviceLabel() {
  if (typeof navigator === "undefined") return "Browser";
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome" : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : "Browser";
  const os = /Windows/.test(ua) ? "Windows" : /Mac OS/.test(ua) ? "Mac" : /Android/.test(ua) ? "Android" : /iPhone|iPad/.test(ua) ? "iOS" : /Linux/.test(ua) ? "Linux" : "";
  return `${browser}${os ? ` on ${os}` : ""}`;
}
